/**
 * Application Entry Point
 * Bootstraps the React application with providers.
 * @packageDocumentation
 */

import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './i18n';
import './index.css';
import { router } from './routes';
import { useAuthStore } from './stores/auth';
import { useEnvironmentStore } from './stores';
import { trpc, createTRpcClient } from './api/tRpc';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { resetTenantScopedQueries } from './lib/query-tenant';
import { reloadOnceForNewBuild } from './lib/stale-build-reload';

/** Query client configuration with default options. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Reset tenant-scoped query cache when the retailer/environment changes.
// Tenant travels via request headers (see api/tRpc.ts), not the query key, so the cache
// must be actively evicted on a switch — otherwise the previous tenant's data is served
// until the next poll. resetTenantScopedQueries cancels in-flight requests, clears the
// tenant-scoped cache (surfacing each page's loading state), and refetches with the new
// headers, while preserving the tenant-independent retailers list.
useEnvironmentStore.subscribe((state, prevState) => {
  if (
    state.currentRetailerCode !== prevState.currentRetailerCode ||
    state.currentEnvironment !== prevState.currentEnvironment
  ) {
    resetTenantScopedQueries(queryClient);
  }
});

// After reauth: cancel blocked queries then refetch with new session
useAuthStore.subscribe((state, prevState) => {
  if (!state.sessionExpired && prevState.sessionExpired) {
    queryClient.cancelQueries().then(() => queryClient.invalidateQueries());
  }
});

// A tab opened before a deploy requests chunks the new image no longer has. Reload once to pick up
// the new build. preventDefault stops Vite rethrowing, so the page stays blank until the reload
// instead of flashing the route error screen; when no reload is started, the error is shown.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForNewBuild()) {
    event.preventDefault();
  }
});

/** tRPC client (created once, shared via Provider) */
const trpcClient = createTRpcClient();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  </StrictMode>,
);
